import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const WS_URL = 'wss://8vved4kfgg.execute-api.us-east-1.amazonaws.com/prod';
const SITE_URL = 'https://werewolf.bykapil.com';

const ROLE_INFO = {
  werewolf: { emoji: '🐺', color: '#e74c3c', desc: 'Kill a villager each night' },
  healer: { emoji: '💚', color: '#2ecc71', desc: 'Protect one player each night' },
  seer: { emoji: '🔮', color: '#9b59b6', desc: 'Investigate one player each night. You cannot be killed by werewolves!' },
  villager: { emoji: '🏘️', color: '#3498db', desc: 'Find and vote out the werewolves' }
};

function useWebSocket(onMessage) {
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(WS_URL);
    ws.onopen = () => console.log('WS connected');
    ws.onmessage = (e) => { try { onMessage(JSON.parse(e.data)); } catch(err) { console.error(err); } };
    ws.onclose = () => { reconnectRef.current = setTimeout(connect, 2000); };
    wsRef.current = ws;
  }, [onMessage]);

  const sendMsg = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify(data));
  }, []);

  useEffect(() => { connect(); return () => { clearTimeout(reconnectRef.current); wsRef.current?.close(); }; }, [connect]);

  return sendMsg;
}

export default function App() {
  const [screen, setScreen] = useState('home');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [gameId, setGameId] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [players, setPlayers] = useState({});
  const [adminId, setAdminId] = useState('');
  const [role, setRole] = useState(null);
  const [phase, setPhase] = useState(null);
  const [round, setRound] = useState(0);
  const [phaseEnd, setPhaseEnd] = useState(0);
  const [timer, setTimer] = useState(0);
  const [votes, setVotes] = useState({});
  const [myVote, setMyVote] = useState(null);
  const [myAction, setMyAction] = useState(null);
  const [chatLog, setChatLog] = useState([]);
  const [wolfVotes, setWolfVotes] = useState({});
  const [fellowWolves, setFellowWolves] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [eliminated, setEliminated] = useState([]);
  const [seerResult, setSeerResult] = useState(null);
  const [nightResult, setNightResult] = useState(null);
  const [gameOver, setGameOver] = useState(null);
  const [error, setError] = useState('');
  const [openGames, setOpenGames] = useState([]);
  const [popup, setPopup] = useState(null);
  const chatEndRef = useRef(null);

  const showPopup = (msg) => { setPopup(msg); setTimeout(() => setPopup(null), 5000); };

  const onMessage = useCallback((msg) => {
    console.log('MSG:', msg);
    switch(msg.type) {
      case 'gameCreated':
        setGameId(msg.gameId); setPlayerId(msg.playerId); setPlayers(msg.players); setAdminId(msg.playerId);
        localStorage.setItem('ww_game', JSON.stringify({gameId: msg.gameId, playerId: msg.playerId}));
        setScreen('lobby'); break;
      case 'gameJoined':
        setGameId(msg.gameId); setPlayerId(msg.playerId); setPlayers(msg.players); setAdminId(msg.adminId);
        localStorage.setItem('ww_game', JSON.stringify({gameId: msg.gameId, playerId: msg.playerId}));
        setScreen('lobby'); break;
      case 'playerJoined': case 'playerReconnected': case 'playerDisconnected':
        if (msg.players) setPlayers(msg.players); break;
      case 'rejoined':
        setGameId(msg.gameId); setPlayerId(msg.playerId); setPlayers(msg.players); setAdminId(msg.adminId);
        setRole(msg.role); setPhase(msg.phase); setRound(msg.round); setPhaseEnd(msg.phaseEndTime);
        setVotes(msg.votes || {}); setChatLog(msg.chatLog || []); setEliminated(msg.eliminatedLog || []);
        setScreen(msg.status === 'lobby' ? 'lobby' : 'game'); break;
      case 'gameStarted':
        setRole(msg.role); setPlayers(msg.players); setPhase(msg.phase); setPhaseEnd(msg.phaseEndTime);
        setRound(msg.round); setScreen('game');
        if (msg.fellowWolves) setFellowWolves(msg.fellowWolves);
        break;
      case 'phaseChange':
        setPhase(msg.phase); setPhaseEnd(msg.phaseEndTime); setRound(msg.round);
        setMyVote(null); setMyAction(null); setVotes({}); setSeerResult(null); setNightResult(null); setWolfVotes({}); break;
      case 'chatMessage':
        setChatLog(prev => [...prev.slice(-50), msg]); break;
      case 'voteUpdate':
        setVotes(msg.voteCounts || {}); break;
      case 'actionConfirmed':
        setMyAction(msg.target); break;
      case 'wolfVoteUpdate':
        setWolfVotes(msg.wolfVotes || {}); break;
      case 'seerResult':
        setSeerResult(msg); break;
      case 'nightResult':
        setNightResult(msg);
        if (msg.killed) {
          setPlayers(prev => ({...prev, [msg.killed]: {...prev[msg.killed], alive: false}}));
          setEliminated(prev => [...prev, {playerId: msg.killed, name: msg.killedName, role: msg.killedRole, phase: 'night'}]);
          showPopup(`🐺 The werewolves killed ${msg.killedName} (${msg.killedRole}).\n☀️ Day is setting in...`);
        } else {
          showPopup(`🌙 No one was killed tonight.\n☀️ Day is setting in...`);
        }
        break;
      case 'playerEliminated':
        setEliminated(prev => [...prev, msg]);
        setPlayers(prev => ({...prev, [msg.playerId]: {...prev[msg.playerId], alive: false}}));
        showPopup(`🗳️ Vote Tally: ${msg.tally || 'N/A'}\n\n${msg.name} (${msg.role}) was voted out!\n🌙 Night is setting in...`);
        break;
      case 'noElimination':
        showPopup(`🗳️ Vote Tally: ${msg.tally || 'No votes'}\n\nNo majority reached — no one was eliminated.\n🌙 Night is setting in...`);
        break;
      case 'gameOver':
        setGameOver(msg); localStorage.removeItem('ww_game'); break;
      case 'error':
        setError(msg.message); setTimeout(() => setError(''), 3000); break;
      case 'gamesList':
        setOpenGames(msg.games || []); break;
      default: break;
    }
  }, []);

  const sendMsg = useWebSocket(onMessage);

  // Rejoin on mount
  useEffect(() => {
    const saved = localStorage.getItem('ww_game');
    if (saved) {
      const {gameId: gid, playerId: pid} = JSON.parse(saved);
      setTimeout(() => sendMsg({action: 'rejoinGame', gameId: gid, playerId: pid}), 500);
    }
  }, [sendMsg]);

  // Timer
  useEffect(() => {
    if (!phaseEnd) return;
    let forceSent = false;
    let nightForceSent = false;
    const iv = setInterval(() => {
      const left = Math.max(0, phaseEnd - Math.floor(Date.now()/1000));
      setTimer(left);
      if (left <= 5 && !forceSent && gameId && playerId) {
        forceSent = true;
        sendMsg({action: 'forceVotes', gameId, playerId});
      }
      if (left <= 10 && !nightForceSent && gameId && playerId) {
        nightForceSent = true;
        sendMsg({action: 'forceNightActions', gameId, playerId});
      }
      if (left === 0) {
        clearInterval(iv);
        if (gameId && playerId) sendMsg({action: 'endPhase', gameId, playerId});
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [phaseEnd, gameId, playerId, sendMsg]);

  // Auto-scroll chat
  useEffect(() => { chatEndRef.current?.scrollIntoView({behavior: 'smooth'}); }, [chatLog]);

  const createGame = () => { if (name.trim()) sendMsg({action: 'createGame', name: name.trim()}); };
  const joinGame = () => { if (name.trim() && code.trim()) sendMsg({action: 'joinGame', gameId: code.trim().toUpperCase(), name: name.trim()}); };
  const startGame = () => sendMsg({action: 'startGame', gameId, playerId});
  const leaveGame = () => { localStorage.removeItem('ww_game'); setScreen('home'); setGameId(''); setPlayerId(''); setPlayers({}); setRole(null); setPhase(null); setChatLog([]); setEliminated([]); setGameOver(null); };
  const sendChat = () => { if (chatInput.trim()) { sendMsg({action: 'chat', gameId, playerId, message: chatInput.trim()}); setChatInput(''); } };
  const vote = (targetId) => { setMyVote(targetId); sendMsg({action: 'vote', gameId, playerId, targetId}); };
  const nightAction = (targetId) => { setMyAction(targetId); sendMsg({action: 'nightAction', gameId, playerId, targetId}); };

  const shareUrl = `${SITE_URL}?join=${gameId}`;
  const amIAlive = players[playerId]?.alive !== false;

  // Check URL for join code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const joinCode = params.get('join');
    if (joinCode) { setCode(joinCode); setScreen('join'); window.history.replaceState({}, '', '/'); }
  }, []);

  // Fetch open games when on home screen
  useEffect(() => {
    if (screen === 'home') {
      setTimeout(() => sendMsg({action: 'listGames'}), 500);
    }
  }, [screen, sendMsg]);

  if (screen === 'home') return (
    <div className="app">
      <div className="home">
        <h1 className="logo">🐺 Werewolf</h1>
        <p className="tagline">Play with your friends</p>
        <button className="btn primary" onClick={() => setScreen('create')}>🎮 Create Game</button>
        <button className="btn secondary" onClick={() => setScreen('join')}>🔗 Join Game</button>
        {openGames.length > 0 && (
          <div className="open-games">
            <h3>Open Games</h3>
            {openGames.map(g => (
              <div key={g.gameId} className="open-game-row" onClick={() => { setCode(g.gameId); setScreen('join'); }}>
                <span className="og-host">🎮 {g.host}</span>
                <span className="og-players">{g.players}/6</span>
                <span className="og-code">{g.gameId}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (screen === 'create') return (
    <div className="app">
      <div className="panel">
        <h2>Create Game</h2>
        <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} maxLength={20} onKeyDown={e => e.key === 'Enter' && createGame()} />
        <button className="btn primary" onClick={createGame} disabled={!name.trim()}>Create</button>
        <button className="btn ghost" onClick={() => setScreen('home')}>← Back</button>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );

  if (screen === 'join') return (
    <div className="app">
      <div className="panel">
        <h2>Join Game</h2>
        <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} maxLength={20} />
        <input placeholder="Game code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} maxLength={6} onKeyDown={e => e.key === 'Enter' && joinGame()} />
        <button className="btn primary" onClick={joinGame} disabled={!name.trim() || !code.trim()}>Join</button>
        <button className="btn ghost" onClick={() => setScreen('home')}>← Back</button>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );

  if (screen === 'lobby') return (
    <div className="app">
      <div className="panel lobby">
        <h2>🐺 Game Lobby</h2>
        <div className="code-box">
          <span className="code-label">Code</span>
          <span className="code-value">{gameId}</span>
        </div>
        <div className="share-row">
          <a className="share-btn wa" href={`https://wa.me/?text=${encodeURIComponent(`Join my Werewolf game! ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>
          <a className="share-btn sms" href={`sms:?body=${encodeURIComponent(`Join my Werewolf game! ${shareUrl}`)}`}>SMS</a>
          <button className="share-btn copy" onClick={() => {navigator.clipboard.writeText(shareUrl); }}>Copy Link</button>
        </div>
        <div className="player-list">
          {Object.entries(players).map(([pid, p]) => (
            <div key={pid} className={`player-tag ${p.connected === false ? 'dc' : ''}`}>
              {p.isBot ? '🤖' : '👤'} {p.name} {pid === adminId && '⭐'}
            </div>
          ))}
        </div>
        <p className="hint">{Object.keys(players).length}/6 players • Empty slots filled by AI bots</p>
        <div className="wait-banner">⏳ Wait for players to join before you start the game</div>
        {playerId === adminId && <button className="btn primary" onClick={startGame}>🚀 Start Game</button>}
        <button className="btn ghost" onClick={leaveGame}>← Leave Game</button>
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  );

  if (gameOver) return (
    <div className="app">
      <div className="panel gameover">
        <h2>{gameOver.winner === 'werewolves' ? '🐺 Werewolves Win!' : '🏘️ Villagers Win!'}</h2>
        <div className="roles-reveal">
          {Object.entries(gameOver.roles || {}).map(([pid, p]) => (
            <div key={pid} className={`reveal-row ${p.alive ? '' : 'dead'}`}>
              <span>{ROLE_INFO[p.role]?.emoji} {p.name}</span>
              <span className="role-badge" style={{background: ROLE_INFO[p.role]?.color}}>{p.role}</span>
            </div>
          ))}
        </div>
        <button className="btn primary" onClick={() => { setGameOver(null); setScreen('home'); setPhase(null); setRole(null); setChatLog([]); setEliminated([]); }}>🏠 Home</button>
      </div>
    </div>
  );

  // Game screen
  const alivePlayers = Object.entries(players).filter(([,p]) => p.alive !== false);
  const deadPlayers = Object.entries(players).filter(([,p]) => p.alive === false);

  return (
    <div className={`app game-screen ${phase === 'night' ? 'night' : 'day-phase'}`}>
      {/* Header */}
      <div className="game-header">
        <div className="role-badge-sm" style={{background: ROLE_INFO[role]?.color}}>
          {ROLE_INFO[role]?.emoji} {role}
        </div>
        <div className="phase-info">
          {phase === 'settle' && '🌅 Get Ready'}
          {phase === 'day' && `☀️ Day ${round}`}
          {phase === 'night' && `🌙 Night ${round}`}
        </div>
        <div className="timer" style={{color: timer <= 10 ? '#e74c3c' : '#fff'}}>{timer}s</div>
        <button className="leave-btn" onClick={leaveGame}>✕</button>
      </div>

      {/* Role reveal on settle */}
      {phase === 'settle' && (
        <div className="settle-reveal">
          <div className="role-card" style={{borderColor: ROLE_INFO[role]?.color}}>
            <span className="role-emoji-lg">{ROLE_INFO[role]?.emoji}</span>
            <h3>You are a {role}</h3>
            <p>{ROLE_INFO[role]?.desc}</p>
          </div>
        </div>
      )}

      {/* Night result banner */}
      {nightResult && phase === 'day' && (
        <div className="night-banner">
          {nightResult.killed ? `💀 ${nightResult.killedName} was killed (${nightResult.killedRole})` : '🌙 No one was killed tonight'}
        </div>
      )}

      {/* Seer result */}
      {seerResult && phase === 'night' && (
        <div className="seer-banner">
          🔮 {seerResult.targetName} is {seerResult.isWerewolf ? '🐺 a Werewolf!' : '✅ not a Werewolf'}
        </div>
      )}

      {/* Players grid */}
      <div className="players-grid">
        {alivePlayers.map(([pid, p]) => {
          const isWolf = fellowWolves.includes(pid);
          const isNightWolf = phase === 'night' && role === 'werewolf' && isWolf;
          const wolfTarget = wolfVotes[pid] ? players[wolfVotes[pid]]?.name : null;
          const seerChecked = seerResult && seerResult.target === pid;
          const seerIcon = seerChecked ? (seerResult.isWerewolf ? '🐺' : '✅') : null;
          return (
          <div key={pid} className={`player-card ${myVote === pid || myAction === pid ? 'selected' : ''} ${pid === playerId ? 'me' : ''} ${votes[pid] ? 'has-votes' : ''} ${isNightWolf ? 'wolf-ally' : ''} ${seerChecked ? (seerResult.isWerewolf ? 'seer-wolf' : 'seer-safe') : ''}`}
            onClick={() => {
              if (!amIAlive) return;
              if (pid === playerId && !(phase === 'night' && role === 'healer')) return;
              if (phase === 'day') vote(pid);
              if (phase === 'night' && role !== 'villager') {
                if (role === 'werewolf' && fellowWolves.includes(pid)) return;
                if (role === 'seer' && myAction) return; // Seer can't change
                nightAction(pid); // Werewolf and healer can change
              }
            }}>
            <span className="player-avatar">{seerIcon || (isNightWolf ? '🐺' : p.isBot ? '🤖' : '👤')}</span>
            <span className="player-name">{p.name}{pid === playerId ? ' (you)' : ''}</span>
            {phase === 'day' && votes[pid] && <span className="vote-count">{votes[pid]} votes</span>}
            {phase === 'night' && role === 'werewolf' && isWolf && wolfTarget && <span className="wolf-target">🎯 {wolfTarget}</span>}
            {seerChecked && <span className="seer-label">{seerResult.isWerewolf ? 'Werewolf!' : 'Not a wolf'}</span>}
          </div>
          );
        })}
      </div>

      {/* Dead players */}
      {eliminated.length > 0 && (
        <div className="dead-section">
          <span className="dead-label">💀 Eliminated:</span>
          {eliminated.map((e, i) => (
            <span key={i} className="dead-tag">{ROLE_INFO[e.role]?.emoji} {e.name}</span>
          ))}
        </div>
      )}

      {/* Action hint */}
      {amIAlive && phase === 'day' && !myVote && <p className="action-hint">👆 Tap a player to vote them out</p>}
      {amIAlive && phase === 'day' && myVote && <p className="action-hint">👆 Tap another player to change your vote</p>}
      {amIAlive && phase === 'night' && !myAction && role === 'werewolf' && <p className="action-hint">🐺 Choose someone to kill</p>}
      {amIAlive && phase === 'night' && !myAction && role === 'seer' && <p className="action-hint">🔮 Choose someone to investigate</p>}
      {amIAlive && phase === 'night' && !myAction && role === 'healer' && <p className="action-hint">💚 Choose someone to protect</p>}
      {amIAlive && phase === 'night' && myAction && role === 'healer' && <p className="action-hint healer-confirm">💚 {players[myAction]?.name || 'Someone'} will be saved. Tap another to change.</p>}
      {amIAlive && phase === 'night' && role === 'villager' && <p className="action-hint">😴 Sleep tight — nothing to do at night</p>}
      {!amIAlive && <p className="action-hint">👻 You are a spectator</p>}

      {/* Chat */}
      <div className="chat-box">
        <div className="chat-messages">
          {chatLog.map((m, i) => (
            <div key={i} className={`chat-msg ${m.sender === playerId ? 'mine' : ''}`}>
              <strong>{m.name}:</strong> {m.message}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        {amIAlive && (phase === 'day' || phase === 'settle') && (
          <div className="chat-input-row">
            <input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Type a message..." onKeyDown={e => e.key === 'Enter' && sendChat()} />
            <button onClick={sendChat}>Send</button>
          </div>
        )}
      </div>

      {error && <p className="error floating">{error}</p>}
      {popup && <div className="phase-popup"><p>{popup}</p></div>}
    </div>
  );
}
